import { useMutation, useQuery } from "@tanstack/react-query";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { DivisioninventoriesColumns, InterDivisioninventoriesColumns } from "./columns";
import { useState } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTeamValue } from "providers/TeamContext";
import dayjs from "dayjs";
import { Grid } from "@mui/material";
import { LoadingDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import { ExportDialog } from "ui-components/Alert";
import { SelectField } from "../components/Form";
import { SelectFields } from "ui-components/Form";
import { getDivisionGroupOptions } from "services/data.internal.service";
import { getDivisionInventoriesEnquiry } from "services/data.report.service";
import { syncDivisionBySKU } from "services/data.inventory.service";
import { syncDivisionByCustomer } from "services/data.inventory.service";
import { getDivisionCustomers } from "services/data.inventory.service";
import { SyncButton } from "ui-components/IconButton";

function DivisionInventoriesEnquiry() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const exportPermission =
    allowAll ||
    (Boolean(permissions["Division Inventories_Report"]) &&
      permissions["Division Inventories_Report"].findIndex((b) => b === "export") !== -1);

  const [alert, setAlert] = useState(null);
  const { isDownloading, downloadCsv } = useCsvDownloadHook();

  const [divisionGroupOpt, setDivisionGroupOpt] = useState([]);
  const [selectedDivisionGroup, setSelectedDivisionGroup] = useState("");
  const [customerCodeOpt, setCustomerCodeOpt] = useState([]);
  const [selectedCustomerCode, setSelectedCustomerCode] = useState("");
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  useQuery({
    queryKey: ["division-group-options", { customerCode }],
    queryFn: () => getDivisionGroupOptions({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      const options = data.data.data.map((v) => v);
      setDivisionGroupOpt(options);
    }
  })

  useQuery({
    queryKey: ["customer-division-list", { customerCode }],
    enabled: Boolean(selectedDivisionGroup),
    queryFn: () =>
      getDivisionCustomers({
        divisionGroup: selectedDivisionGroup,
      }),
    onSuccess: (data) => {
      let result = data.data.data;
      setCustomerCodeOpt(result);
    },
  })

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const [columns, setColumns] = useState(DivisioninventoriesColumns);

  const syncMutation = useMutation(syncDivisionBySKU, {
    onSuccess: (data) => {
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const syncCustomerMutation = useMutation(syncDivisionByCustomer, {
    onSuccess: (data) => {
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const handleSyncCustomer = async () => {
    syncCustomerMutation.mutate({ divisionGroup: selectedDivisionGroup, customerCode: selectedCustomerCode })
  }

  const { refetch, isFetching } = useQuery({
    queryKey: ["division-inventories-enquiry", { customerCode, selectedDivisionGroup }],
    queryFn: () => getDivisionInventoriesEnquiry({ customerCode, selectedDivisionGroup }),
    enabled: Boolean(customerCode) && Boolean(selectedDivisionGroup),
    onSuccess: (data) => {
      const result = data.data.data;
      const customers = [...new Set(result.map(x => x.customerCode))];
      setColumns([
        ...DivisioninventoriesColumns,
        ...customers.map(c => ({
          header: c,
          accessorKey: c
        })),
        {
          header: "Total Division Quantity",
          accessorKey: "totalDivisionQty"
        },
        {
          header: "RPG GROUP Balance",
          accessorKey: "rpgBalance"
        },
        {
          header: "Action",
          accessorKey: "action",
          width: "100px",
          enableColumnFilter: false,
          cell: ({ row }) => {
            return (
              <SyncButton
                title="sync inventory"
                onClick={() => {
                  syncMutation.mutate({
                    divisionGroup: selectedDivisionGroup,
                    skuCode: row.original.skuCode
                  });
                }}
              />
            );
          },
        }
      ]);
      const flattened = Object.values(
        result.reduce((acc, row) => {
          const key = `${row.skuCode}`;

          if (!acc[key]) {
            acc[key] = {
              skuCode: row.skuCode
            };

            customers.forEach(c => acc[key][c] = 0);
          }

          acc[key][row.customerCode] += Number(row.quantity);

          return acc;
        }, {})
      ).map(row => {
        const rpgQty = Number(row["RPG GROUP"] || 0);

        let otherTotal = 0;
        customers.forEach(c => {
          if (c !== "RPG GROUP") {
            otherTotal += Number(row[c] || 0);
          }
        });

        row.totalDivisionQty = String(otherTotal);
        row.rpgBalance = String(rpgQty - otherTotal);

        // convert customer columns to string
        customers.forEach(c => {
          row[c] = String(row[c]);
        });

        return row;
      });

      setData(flattened);
      setFilteredData(flattened);

    },
    onError: () => { },
  });
  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}
      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem 0 0 1rem">
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <SelectFields
                field={{
                  name: "divisionGroup",
                  label: "Division Group",
                  value: selectedDivisionGroup,
                  errors: {},
                }}
                options={divisionGroupOpt}
                onChange={(e, option) => {
                  setSelectedDivisionGroup(option)
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <MDButton
                sx={{ marginLeft: "1rem" }}
                variant="gradient"
                color="info"
                disabled={!exportPermission}
                onClick={() => {
                  const filename = `${customerCode}_${dayjs(new Date()).format(
                    "YYYYMMDD-HHmm"
                  )}_-Division-Inventories`;
                  downloadCsv(
                    filename,
                    filteredData.map((x) => {
                      const { ...d } = x;
                      return {
                        ...d
                      };
                    })
                  );
                }}
              >
                Export
              </MDButton>
            </Grid>
            <Grid item xs={4}>
              <Grid justifyContent="flex-end" container spacing={3} sx={{ width: "100%" }}>
                <Grid item xs={8}>
                  <SelectFields
                    field={{
                      name: "customerCode",
                      label: "Customer Code",
                      value: selectedCustomerCode,
                      errors: {},
                    }}
                    options={customerCodeOpt}
                    onChange={(e, option) => {
                      setSelectedCustomerCode(option)
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MDButton color="info" onClick={handleSyncCustomer} disabled={!Boolean(selectedCustomerCode)}>
                    sync
                  </MDButton>
                </Grid>
              </Grid>

            </Grid>
          </Grid>
        </MDBox>
        <BasicTable
          data={data}
          columns={columns}
          onFilterRowsChange={handleFilterRowsChange}
        />
      </TableCard>
    </ListLayout>
  );
}

export default DivisionInventoriesEnquiry;
